import React, { useRef, useEffect, useCallback, useMemo } from 'react';

import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import styled from 'styled-components';


import EmailService from '../../../../../../services/Api/Email';
import PagarmeService from '../../../../../../services/Api/Pagarme';
import Url from '../../../../../../services/Url';
import Utils from '../../../../../../services/Utils';

import connectAuth, {
    IAuthMapStateToProps,
    IConnectedProps
} from '../../hooks/connectAuth';

import Button from '../../../Common/Form/Button';
import PaymentModal, {
    PAYMENT_MODAL_ID
} from '../../../PaymentModal/PaymentModal';
import { ESubscriptionAppRedirectOrigin } from '../../../Paywall/common/SubscriptionAppRedirect';
import { EVENTS, CHECKOUT_PARAMS } from '../../Constants';
import { Types } from '../../Store/ContainerReducer';
import { IAuthState } from '../../Store/Reducers';

declare global {
    interface Document {
        openPaymentModal: () => void;
    }
}

interface IPayment {
    planId: string,
    amount: string,
    userName: string,
    userEmail: string,
    showModal: boolean,
    reference: string,
    listenTo?: string,
    i18nPayment?: boolean,
    userFullName: string,
    isSubscriber: boolean,
    urlReference: string,
    communication: string,
    renewalPlanId: string,
    pagarmeResponse: any,
    isEmailConfirmed: boolean,
    buttonPaymentText: string,
    isNext?: string;
}

type TPayment = IConnectedProps<IPayment>;

interface IPagarmeCheckoutSuccess {
    (reponse: any): void;
}

const Welcome = styled.h2`
  text-align: center;
  line-height: 1.75rem;
  font-size: 1.4375rem !important;
  color: ${({ theme }) =>
        theme.name === 'dark' ? theme.colors.white : theme.colors.gray900};
`;

const PaymentContainer = styled.div`
  z-index: 2;
  display: flex;
  min-width: 100%;
  position: relative;
  align-items: center;
  max-width: 21.0625em;
  box-sizing: border-box;
  flex-direction: column;
  justify-content: center;
  gap: ${({ theme }) => theme.space.cat};
  padding: ${({ theme }) => theme.space.mouse};
  border-radius: ${({ theme }) => theme.radii[2]};
  font-size: ${({ theme }) => theme.fontSizes.mouse};
`;

const Payment: React.FC<TPayment> = ({ intl, dispatch, ...props }) => {
    const formRef = useRef<HTMLFormElement>(null);

    const $PagarmeService = useMemo(() => new PagarmeService(), []);
    const $EmailService = useMemo(() => new EmailService(), []);

    const {
        planId,
        amount,
        listenTo,
        userName,
        userEmail,
        showModal,
        reference,
        userFullName,
        isSubscriber,
        urlReference,
        communication,
        renewalPlanId,
        pagarmeResponse,
        isEmailConfirmed,
        buttonPaymentText,
        i18nPayment,
        isNext
    } = props;

    ($PagarmeService.callbackCheckoutSuccess as IPagarmeCheckoutSuccess) = (
        response
    ) =>
        dispatch({
            type: Types.PAGARME_CHECKOUT,
            data: { pagarmeResponse: JSON.stringify(response) }
        });

    const pagarmeCheckout = useCallback(
        () =>
            $PagarmeService.checkout({
                ...CHECKOUT_PARAMS,
                amount: parseInt(amount),
                buttonText: intl.formatMessage({
                    id: 'authFunctional.payment.continue'
                })
            }),
        [$PagarmeService, amount, intl]
    );

    const openPaymentModal = useCallback(() => {
        const event = new CustomEvent(EVENTS.AUTH_FUNCTIONAL_CHECKOUT);
        document.dispatchEvent(event);

        document.openPaymentModal();
    }, [listenTo, pagarmeCheckout]);

    const handleClickPayment = () => {
        if (isNext) {
            Url.location(
                intl.formatMessage({ id: 'authFunctional.payment.paymentUrl' })
            );
            return;
        }

        if (isSubscriber) {
            Url.location(
                intl.formatMessage({ id: 'authFunctional.payment.birthChartUrl' })
            );
            return;
        }

        openPaymentModal();
    };

    const handleClickFreeBirthchart = async () => {
        if (!isEmailConfirmed) {
            await $EmailService.resendConfirmation();
        }
        Url.location(
            intl.formatMessage({ id: 'authFunctional.payment.nativeUrl' })
        );
    };

    useEffect(() => {
        if (!pagarmeResponse || !formRef.current) return;

        formRef.current.submit();
    }, [pagarmeResponse]);

    useEffect(() => {
        if (!showModal) return;

        setTimeout(() => openPaymentModal(), 0);
    }, [showModal, openPaymentModal]);

    const paymentComponent = useMemo(() => {
        if (Utils.isInternational() && i18nPayment) {
            return (
                <PaymentModal
                    name={userFullName}
                    email={userEmail}
                    appRedirectOrigin={ESubscriptionAppRedirectOrigin.BIRTH_CHART_COMPLETE}
                />
            );
        }

        return (
            <form ref={formRef} method="POST" action="/checkout">
                <input type="hidden" name="planid" value={planId} />
                <input type="hidden" name="confirmEmail" value="true" />
                <input type="hidden" name="reference" value={reference} />
                <input type="hidden" name="pagarme" value={pagarmeResponse} />
                <input type="hidden" name="renewPlanId" value={renewalPlanId} />
                <input type="hidden" name="redirectRoute" value={urlReference} />
                <input type="hidden" name="renewalPlanId" value={renewalPlanId} />
            </form>
        );
    }, [
        planId,
        reference,
        userEmail,
        userFullName,
        urlReference,
        renewalPlanId,
        pagarmeResponse
    ]);

    return (
        <PaymentContainer>
            <Welcome>
                <FormattedHTMLMessage
                    id="authFunctional.payment.welcome"
                    values={{ userName }}
                />
            </Welcome>

            {communication && <p>{communication}</p>}

            <Button variation="text" onClick={handleClickFreeBirthchart}>
                {intl.formatMessage({ id: 'authFunctional.payment.link' })}
            </Button>

            {!isSubscriber && (
                <Button onClick={handleClickPayment}>
                    {buttonPaymentText ? (
                        <span>{buttonPaymentText}</span>
                    ) : (
                        <span>
                            <FormattedMessage id="authFunctional.payment.button" />
                        </span>
                    )}
                </Button>
            )}

            {paymentComponent}
        </PaymentContainer>
    );
};

const mapStateToProps: IAuthMapStateToProps<IPayment> = (
    state,
    isComparison = false
) =>
    !isComparison
        ? {
            listenTo: (state as IAuthState).Container.listenTo,
            userName: (state as IAuthState).Container.user.name,
            userEmail: (state as IAuthState).Container.user.email,
            planId: (state as IAuthState).Container.pagarme.planId,
            amount: (state as IAuthState).Container.pagarme.amount,
            i18nPayment: (state as IAuthState).Container.i18nPayment,
            userFullName: (state as IAuthState).Container.user.fullName,
            showModal: (state as IAuthState).Container.pagarme.showModal,
            reference: (state as IAuthState).Container.pagarme.reference,
            isSubscriber: (state as IAuthState).Container.user.isSubscriber,
            urlReference: (state as IAuthState).Container.pagarme.urlReference,
            communication: (state as IAuthState).Container.pagarme.comunication,
            renewalPlanId: (state as IAuthState).Container.pagarme.renewalPlanId,
            pagarmeResponse: (state as IAuthState).Container.pagarme
                .pagarmeResponse,
            isEmailConfirmed: (state as IAuthState).Container.user.isEmailConfirmed,
            buttonPaymentText: (state as IAuthState).Container.communication
                .buttonPaymentText,
            isNext: (state as IAuthState).Container.communication.isNext
        }
        : {
            planId: state.planId,
            amount: state.amount,
            listenTo: state.listenTo,
            i18nPayment: state.i18nPayment,
            userName: state.userName,
            userEmail: state.userEmail,
            showModal: state.showModal,
            reference: state.reference,
            userFullName: state.userFullName,
            isSubscriber: state.isSubscriber,
            urlReference: state.urlReference,
            communication: state.communication,
            renewalPlanId: state.renewalPlanId,
            pagarmeResponse: state.pagarmeResponse,
            isEmailConfirmed: state.isEmailConfirmed,
            buttonPaymentText: state.buttonPaymentText,
            isNext: state.isNext
        };

export default connectAuth(mapStateToProps)(Payment);
